import React, { useState, useEffect } from "react";
import { Button } from "../../components/Button";
import { TemplateTasks } from "./TemplateTasks";
import workflowApi from "../../api/workflowApi";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import FormField from "../../components/ui/FormField";

export const CreateWorkflowTemplateModalCreate = ({
    setShowCreateTemplate,
  onTemplateCreated,
  templateToEdit,
}) => {
  const token = useSelector((state) => state.auth.access_token);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [tasks, setTasks] = useState([]);
  const [errors, setErrors] = useState({});
  const safeTrim = (value) => (value || "").trim();

  const isEditing = !!templateToEdit;

  useEffect(() => {
    if (isEditing && templateToEdit) {
      setName(templateToEdit.name || "");
      setDescription(templateToEdit.description || "");
      setCategory(templateToEdit.category || "");
      setTasks(templateToEdit.tasks);
    } else {
      setName("");
      setDescription("");
      setCategory("");
      setTasks([
        {
          title: "",
          description: "",
          attachment_list_json: null,
          priority: "MEDIUM",
          status: "TODO",
          startDate: "",
          dueDate: "",
        },
      ]);
    }
  }, [templateToEdit, isEditing]);

  const validateForm = () => {
    const newErrors = {};

    // ---- Template validation ----
    if (!safeTrim(name)) {
      newErrors.name = "Template Name is required";
    }
    if (!safeTrim(category)) {
      newErrors.category = "Category is required";
    }
    if (!safeTrim(description)) {
      newErrors.description = "Description is required";
    }

    // ---- Task validation ----
    const taskErrors = [];

    tasks.forEach((task, index) => {
      const errors = {};

      if (!safeTrim(task.title)) {
        errors.title = "Task title is required";
      }

      if (!task.priority) {
        errors.priority = "Priority is required";
      }

      if (!task.startDate) {
        errors.startDate = "Start date is required";
      }

      if (!task.dueDate) {
        errors.dueDate = "Due date is required";
      } else if (
        task.startDate &&
        new Date(task.dueDate) < new Date(task.startDate)
      ) {
        errors.dueDate = "Due date cannot be before start date";
      }

      if (!safeTrim(task.description)) {
        errors.description = "Task description is required";
      }

      if (Object.keys(errors).length > 0) {
        taskErrors[index] = errors;
      }
    });

    if (taskErrors.length > 0) {
      newErrors.tasks = taskErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSaveTemplate = async () => {
  if (!validateForm()) {
        toast.error('Please complete existing Workflow template details first');
        return;
    }

    try {
      if (isEditing) {
        for (let task of tasks) {
          if (task.id) {
            await workflowApi.updateTemplateTasks(
              templateToEdit.id,
              task.id,
              token,
              task
            );
          } else {
            await workflowApi.createTemplateTasks(
              templateToEdit.id,
              task,
              token
            );
          }
        }
        toast.success("Tasks updated successfully!");
        onTemplateCreated();
        setShowCreateTemplate(false);
      } else {
        const payload = { name, description, category, tasks };
        await workflowApi.createWorkflowTemplate(payload, token);
        toast.success("Workflow template created successfully!");
        onTemplateCreated();
        setShowCreateTemplate(false);
      }
    } catch (err) {
      console.error("Save error:", err);
      // toast.error("Failed to save template/tasks");
    }
  };

  const formatText = (text = "") =>
    text?.toLowerCase()
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

  return (
    <div onClick={()=> setShowCreateTemplate(false)} className="fixed inset-0 bg-black -top-6 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div onClick={(e)=> e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-5xl w-full p-3 max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditing ? "Edit Workflow Template" : "Create Workflow Template"}
          </h3>
          <button
            onClick={()=> setShowCreateTemplate(false)}
            aria-label="Close"
            className="p-1 rounded-sm bg-red-50 hover:bg-red-100 text-red-700"
          >
            <X/>
          </button>
        </div>

        {/* Template Info */}
        <div className="space-y-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <FormField
                label="Template Name"
                value={name}
                onChange={(e) => setName(e)}
                placeholder="Enter Template Name"
                error={errors.name}
                disabled={isEditing}
                required
            />
            <FormField
                label="Category"
                type="dropdown"
                disabled={isEditing}
                value={category}
                options={['USER_DEFINED_TEMPLATE', 'GST_COMPLIANCE', 'TDS_MANAGEMENT', 'AUDIT_AND_ASSURANCE', 'CLIENT_ONBOARDING']}
                onChange={(e) => setCategory(e)}
                error={errors?.category}
                required
            />
          </div>
          <div className="mt-1">
            <FormField
              disabled={isEditing}
              label="Description"
              type="description"
              rows="2"
              required
              value={description}
              onChange={(e) => setDescription(e)}
              error={errors?.description}
            />
          </div>
            
          {/* Tasks */}
          <TemplateTasks
            initialTasks={tasks}
            workflowId={templateToEdit?.id || null}
            token={token}
            errors={errors}
            onTasksUpdated={(updated) => setTasks(updated)}
          />
        </div>

        {/* Footer */}
        <div className="flex flex-row items-center justify-end space-x-3 mt-2">
          <button
              type="button"
              onClick={()=> setShowCreateTemplate(false)}
              className="bg-red-50 py-2 px-4 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-100"
          >
              Cancel
          </button>
          <Button label="Save Template" onClick={handleSaveTemplate} />
        </div>
      </div>
    </div>
  );
};
